// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { AccordionSectionPresentationProps } from "@steiner/MobiusOverrides/AccordionSection/AccordionSection";
import ManagedAccordionSection from "@steiner/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { FC, useRef } from "react";
import { useReactToPrint } from "react-to-print";

const enum fields {
    prefix = "prefix",
    title = "title",
    sheetCode = "sheetCode",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.prefix]: string;
        [fields.title]: string;
        [fields.sheetCode]: string;
    };
}

type Props = OwnProps;

export interface PipeAndWireSheetStyles {
    tableContainer?: InjectableCss;
    sheetCodeContainer?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    printLink?: LinkPresentationProps;
    printContainer?: InjectableCss;
    strPipeAndWireSheetTitle?: InjectableCss;
}

export const pipeAndWireSheetStyles: PipeAndWireSheetStyles = {};

const styles = pipeAndWireSheetStyles;

const PipeAndWireSheet: FC<Props> = ({ fields: { prefix, title, sheetCode } }) => {
    const pipeAndWireSheetRef = useRef<HTMLDivElement>(null);
    const print = useReactToPrint({
        contentRef: pipeAndWireSheetRef,
        pageStyle: "@media print { @page {}}",
    });

    return (
        <StyledWrapper
            ref={pipeAndWireSheetRef}
            className="str-pipe-and-wire-sheet"
            data-prefix={prefix}
            data-test-selector={`str-pipe-and-wire-sheet-${title}`}
            {...styles.tableContainer}
        >
            <ManagedAccordionSection initialExpanded={true} title={title} {...styles.accordionSection}>
                <StyledWrapper {...styles.printContainer}>
                    <Link
                        {...styles.printLink}
                        onClick={() => {
                            print();
                        }}
                    >
                        {translate("Print Friendly")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper className="str-pipe-and-wire-sheet-body" {...styles.sheetCodeContainer}>
                    <Typography
                        {...styles.strPipeAndWireSheetTitle}
                        className="str-pipe-and-wire-sheet-title"
                    >
                        {title}
                    </Typography>
                    {parse(sheetCode, xParserOptions)}
                </StyledWrapper>
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: PipeAndWireSheet,
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Table Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.prefix,
                displayName: "Prefix (for product id cells)",
                editorTemplate: "TextField",
                defaultValue: "prod_",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.sheetCode,
                displayName: "Sheet Code",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
